/**
 * Turns a markdown table's cell text into grid columns and rows.
 *
 * The agent writes tables as GFM, so every cell arrives as a string: whatever
 * the warehouse column's real type was, it is gone by the time the answer is
 * markdown. Sorting a revenue column lexically ("1,204.5" before "9") is the
 * visible cost of that, so the type is inferred back per column from the body
 * cells and a numeric column gets a value-aware comparator plus the
 * right-alignment a figure column expects.
 *
 * Kept free of ag-Grid imports on purpose: this is the part of the grid
 * treatment that must stay in the main bundle (the fallback and the tests need
 * it) while the grid itself is a lazy chunk.
 */

export type ColumnAlign = 'left' | 'center' | 'right';

export type MdColumn = {
  /** Header cell text. */
  header: string;
  /** Positional key — headers repeat, and a duplicate would silently merge columns. */
  field: string;
  align: ColumnAlign;
  numeric: boolean;
};

/**
 * A cell that reads as a figure: optional sign, an optional currency sigil,
 * digits with `,`/thin-space grouping, an optional fraction and an optional
 * percent sign. Anything else — a date, "12 of 30", prose — is text, so a
 * column containing one is not sorted numerically.
 */
const NUMERIC_CELL = /^[-+]?\s*[£$€¥₹]?\s*\d+(?:[,\s]\d{3})*(?:\.\d+)?\s*%?$/;

export function parseNumericCell(cell: string): number | undefined {
  const value = cell.trim();
  if (!NUMERIC_CELL.test(value)) return undefined;
  const parsed = Number(value.replace(/[^\d.+-]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Sorts by parsed value; cells that are not numbers rank after every number. */
export function compareNumericCells(a: string, b: string): number {
  const left = parseNumericCell(a);
  const right = parseNumericCell(b);
  if (left === undefined && right === undefined) return 0;
  if (left === undefined) return 1;
  if (right === undefined) return -1;
  return left - right;
}

/**
 * Header text with the spaces inside any parenthetical made non-breaking.
 *
 * `wrapHeaderText` breaks a heading at spaces, and left alone it splits "2021 (USD m)" into
 * "2021 (USD" / "m)" — an orphaned unit fragment that reads as a second column. The unit is one
 * token to a reader even though it is two words, so the spaces inside the brackets stop being
 * break opportunities and the only one left is the space before "(", which is exactly where the
 * line should turn.
 *
 * Display only: exports read the source cells, never this.
 */
export function wrappableHeader(header: string): string {
  return header.replace(/\([^()]*\)/g, (parenthetical) => parenthetical.replace(/ /g, '\u00a0'));
}

const columnCount = (rows: string[][]): number =>
  rows.length === 0 ? 0 : Math.max(...rows.map((row) => row.length));

export const fieldFor = (index: number): string => `c${String(index)}`;

/**
 * Column definitions for a table's rows, header row first.
 *
 * `alignments` is the markdown alignment row (`:---`, `:---:`, `---:`) as
 * react-markdown reports it per column; where the author stated one it wins,
 * because it is an explicit instruction rather than an inference.
 */
export function mdColumns(
  rows: string[][],
  alignments: ReadonlyArray<ColumnAlign | undefined>,
): MdColumn[] {
  const [header = [], ...body] = rows;

  return Array.from({ length: columnCount(rows) }, (_, index) => {
    const cells = body.map((row) => (row[index] ?? '').trim()).filter((cell) => cell !== '');
    const numeric = cells.length > 0 && cells.every((cell) => parseNumericCell(cell) !== undefined);

    return {
      header: header[index] ?? '',
      field: fieldFor(index),
      align: alignments[index] ?? (numeric ? 'right' : 'left'),
      numeric,
    };
  });
}

/** Body rows keyed by column field, squared off so a ragged row still renders. */
export function gridRows(rows: string[][]): Array<Record<string, string>> {
  const columns = columnCount(rows);
  const [, ...body] = rows;

  return body.map((row) =>
    Object.fromEntries(
      Array.from({ length: columns }, (_, index) => [fieldFor(index), row[index] ?? '']),
    ),
  );
}
